import { useEffect, useState } from "react";
import { Phone, Calendar, Mail, FileText, Sparkles, MessageCircle, CheckSquare, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const KIND_ICON = {
  note: MessageCircle, call: Phone, meeting: Calendar,
  email: Mail, ai: Sparkles, quote: FileText, task: CheckSquare,
};

const KIND_COLOR = {
  note: "text-gray-600 bg-gray-100",
  call: "text-[#2563EB] bg-blue-50",
  meeting: "text-[#7C3AED] bg-purple-50",
  email: "text-[#059669] bg-emerald-50",
  ai: "text-[#004B87] bg-blue-50",
  quote: "text-[#D97706] bg-amber-50",
  task: "text-gray-600 bg-gray-100",
};

export default function ActivityTimeline({ entityType, entityId }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ kind: "note", title: "", body: "" });

  const load = () => {
    api.get("/activities", { params: { entity_type: entityType, entity_id: entityId } })
      .then((r) => setItems(r.data));
  };

  useEffect(() => {
    if (entityId) load();
    // eslint-disable-next-line
  }, [entityId, entityType]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/activities", {
        entity_type: entityType, entity_id: entityId,
        kind: form.kind, title: form.title, body: form.body,
      });
      setForm({ kind: "note", title: "", body: "" });
      setShowForm(false);
      load();
      toast.success("Activity logged");
    } catch (e) {
      toast.error("Failed to log activity");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await api.delete(`/activities/${id}`);
      load();
    } catch (e) {
      toast.error("Cannot delete");
    }
  };

  return (
    <div data-testid="activity-timeline">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow">Timeline · {items.length}</div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="btn-ghost text-xs"
          data-testid="add-activity-button"
        >
          <Plus size={12} /> Log activity
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="section-card p-3 mb-4 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <select className="input-stark text-sm col-span-1"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              data-testid="activity-kind-select">
              <option value="note">Note</option>
              <option value="call">Call</option>
              <option value="meeting">Meeting</option>
              <option value="email">Email</option>
              <option value="task">Task</option>
            </select>
            <input className="input-stark text-sm col-span-2"
              placeholder="Title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="activity-title-input"
            />
          </div>
          <textarea className="input-stark text-sm min-h-[60px]"
            placeholder="Notes (optional)"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost text-xs" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn-primary text-xs" data-testid="save-activity-button">Save</button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-8">No activities yet.</div>
      ) : (
        <ol className="relative border-l-2 border-gray-200 ml-3 space-y-4">
          {items.map((a) => {
            const Icon = KIND_ICON[a.kind] || MessageCircle;
            const color = KIND_COLOR[a.kind] || "text-gray-600 bg-gray-100";
            return (
              <li key={a.id} className="ml-4 pl-2" data-testid={`activity-${a.id}`}>
                <span className={`absolute -left-3 w-6 h-6 grid place-items-center border border-gray-200 ${color}`}>
                  <Icon size={12} />
                </span>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs uppercase tracking-wider font-bold text-gray-500">{a.kind}</span>
                      <span className="text-xs text-gray-400">{new Date(a.created_at).toLocaleString()}</span>
                      {a.user_name && <span className="text-xs text-gray-400">· {a.user_name}</span>}
                    </div>
                    <div className="font-semibold text-sm mt-1">{a.title}</div>
                    {a.body && <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap line-clamp-4">{a.body}</div>}
                  </div>
                  <button onClick={() => remove(a.id)} className="text-gray-300 hover:text-red-500 ml-2">
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
